import React from 'react';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Button, Collapse } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StyledTextField } from '../productInformation';
import { emptyProductForm } from '../../utils';
import { ComplianceField, EXCEL_ROW_BY_FIELD_NAME } from 'src/Lca/compliance';

interface Props {
  values: typeof emptyProductForm;
  setValues: Function;
}

type ProductForm = typeof emptyProductForm;
type ProductField = keyof ProductForm;

const ADDITIONAL_ENVIRONMENTAL_FIELDS: ProductField[] = [
  'emissions_to_air_water_soil_noise',
  'microplastics_nanoplastics_release',
  'energy_water_resource_consumption',
  'primary_energy_use',
  'recovered_exported_energy',
  'product_performance_factors_impact',
  'deforestation_impact',
  'recycled_materials_share',
  'material_recovery_critical_raw_materials',
  'renewable_materials_content',
  'waste_generated',
  'co2_sequestration_potential',
  'ozone_depletion_potential',
  'acidification_potential',
  'eutrophication_potential',
  'photochemical_ozone_creation_potential',
];

export default function EnvironmentalAdditionalData({
  values,
  setValues,
}: Props) {
  const { t } = useTranslation('lca', {
    keyPrefix: 'lcaInformation.environmentalData',
  });
  const [expanded, setExpanded] = React.useState(false);

  const updateField = (name: ProductField, value: string) => {
    setValues({ ...values, [name]: value });
  };

  const renderField = (name: ProductField) => (
    <ComplianceField
      key={name}
      excelRow={EXCEL_ROW_BY_FIELD_NAME[name]}
      regulatoryFrameworks={values.regulatory_frameworks}
    >
      <StyledTextField
        fullWidth
        label={t(`fields.${name}`)}
        name={name}
        value={typeof values[name] === 'string' ? values[name] : ''}
        onChange={(event) => updateField(name, event.target.value)}
        multiline
        rows={4}
      />
    </ComplianceField>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      {renderField('product_carbon_footprint')}

      <Button
        variant="text"
        onClick={() => setExpanded((current) => !current)}
        sx={{
          alignSelf: 'flex-start',
          p: 0,
          color: '#7e4308',
          fontWeight: 700,
          textTransform: 'none',
        }}
        endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
      >
        {t('showMore')}
      </Button>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' },
            gap: '16px',
          }}
        >
          {ADDITIONAL_ENVIRONMENTAL_FIELDS.map(renderField)}
        </Box>
      </Collapse>
    </Box>
  );
}
